import { apiAddress } from "../../config/server";
import { IPasswordUpdateResponse } from "../../types/auth/authResponses";
import { ApiBase } from "./baseApi";

const AuthApiBase = `${apiAddress}/api/auth/`;

export default class AuthApi extends ApiBase {
  static triggerPasswordReset(externalIdentifier: string): Promise<string> {
    // SECURITY: Validate email format
    if (!externalIdentifier || typeof externalIdentifier !== 'string') {
      return Promise.reject(new Error('Invalid identifier'));
    }

    return ApiBase.get<string>(
      `${AuthApiBase + encodeURIComponent(externalIdentifier)}/reset`,
    ).then((resp) => {
      if (!resp.data) throw new Error(resp.statusText);
      if (!resp.data.success) throw new Error(resp.data.error);
      return resp.data.data;
    });
  }

  static triggerAnonPasswordReset(
    externalIdentifier: string,
    captcha: string,
  ): Promise<string> {
    // SECURITY: Validate email format and captcha
    if (!externalIdentifier || typeof externalIdentifier !== 'string') {
      return Promise.reject(new Error('Invalid username'));
    }

    if (!captcha || typeof captcha !== 'string' || captcha.trim().length === 0) {
      return Promise.reject(new Error('Captcha is required'));
    }

    return ApiBase.post<string>(
      `${AuthApiBase + encodeURIComponent(externalIdentifier)}/unauthedReset`,
      { captcha: captcha.trim() },
    ).then((resp) => {
      if (!resp.data) throw new Error(resp.statusText);
      if (!resp.data.success) throw new Error(resp.data.error);
      return resp.data.data;
    });
  }

  static connectFacebookAccountWithResetToken(
    resetToken: string,
    fbtoken: string,
  ): Promise<boolean> {
    return ApiBase.post<boolean>(`${AuthApiBase}social/facebook/connect`, {
      token: resetToken,
      fbtoken,
    }).then((resp) => resp.data.data);
  }

  static loginWithFacebookToken(token: string) {
    return ApiBase.post<boolean>(`${AuthApiBase}social/facebook/`, {
      fbtoken: token,
    }).then((resp) => resp.data.data);
  }

  static updatePasswordWithToken(
    token: string,
    password: string,
    confirmPassword: string,
  ): Promise<IPasswordUpdateResponse> {
    // SECURITY: Validate token format
    if (!token || typeof token !== 'string' || token.trim().length === 0) {
      return Promise.reject(new Error('Invalid reset token'));
    }

    // SECURITY: Password strength validation
    if (!password || typeof password !== 'string' || password.length < 8) {
      return Promise.reject(new Error('Password must be at least 8 characters long'));
    }

    if (password !== confirmPassword) {
      return Promise.reject(new Error('Passwords do not match'));
    }

    return ApiBase.post<IPasswordUpdateResponse>(`${AuthApiBase}update/token`, {
      token: token.trim(),
      password,
      confirmPassword,
    }).then((resp) => {
      return {
        success: resp.data.success || false,
        message: resp.data.message,
        error: resp.data.error,
      };
    }).catch((error) => {
      return {
        success: false,
        error: error.message || 'Password update failed',
      };
    });
  }
}
